
import { defineComponent } from 'vue';
import { AxiosError } from 'axios';
import { GlobalAlertTypes } from '@momnt-technologies/common-ui/lib/plugins/alerts/types';
import Alert from '~/components/Alerts/Alert.vue';
import CustomButton from '~/components/Buttons/CustomButton.vue';
import PageLoader from '@/components/common/PageLoader.vue';
import PasswordInput from '@/components/common/Inputs/Password.vue';
import { isValidPassword } from '@/utils/validators';
import { createAccountPassword, getInformationFromToken } from '@/api/account';
import { getError, getNonFieldError } from '@/utils/error-handler';

export default defineComponent({
  name: 'CreatePassword',

  components: {
    Alert,
    CustomButton,
    PageLoader,
    PasswordInput,
  },

  data: () => ({
    showAlert: false,
    userName: '',
    userEmail: '',
    password: '',
    confirmPassword: '',
    loadingPage: true,
    loadingSubmit: false,
    errorMessage: '',
  }),

  computed: {
    supportEmail() {
      return this.$store.getters['Config/getBrandSupportEmail'];
    },

    supportPhone() {
      return this.$store.getters['Config/getBrandSupportPhone'];
    },

    validForm() {
      return isValidPassword(this.password) && this.password === this.confirmPassword;
    },
  },

  methods: {
    async submit() {
      this.loadingSubmit = true;
      this.showAlert = false;
      try {
        await createAccountPassword({
          password: this.password,
          token: this.$route.query.token as string
        });

        this.$alerts.add({
          text: 'Password created successfully. Please use your email address and password to login.',
          type: GlobalAlertTypes.SUCCESS,
          timed: true,
          dismissible: true,
        });

        this.$router.push({ name: 'login' });
      } catch (e) {
        const error = e as AxiosError;

        if (error.response?.status === 400) {
          if (getError(e).password) {
            this.errorMessage = getError(e).password[0];
          } else {
            this.showAlert = true;
          }
        } else {
          this.errorMessage = getNonFieldError(e) || 'We are unable to set your password, please try again.';
        }
      } finally {
        this.loadingSubmit = false;
        this.password = '';
        this.confirmPassword = '';
        (this.$refs.pwdForm as HTMLFormElement).resetValidation();
      }
    }
  },

  async created() {
    const token = this.$route.query.token as string;

    try {
      const { data } = await getInformationFromToken(token as string);
      this.userName = data.first_name;
      this.userEmail = data.email;
      this.loadingPage = false;
    } catch (e) {
      const error = e as AxiosError;
      switch (error.response?.status) {
        case 400:
          this.$alerts.add({
            type: GlobalAlertTypes.WARNING,
            timed: true,
            text: 'An account has already been created. Please login using your credentials.'
          });
          this.$router.push({ name: 'login' });
          break;

        default:
          this.$router.push({ name: 'invalid' });
          break;
      }
    }
  }
});
